import React, { createContext, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getReactToken, JWTResponseDTO, logoutSession, validateToken } from '../api/auth.api';
import { setAuthToken } from '../providers/authTokenProvider';
import { getEnvironmentVariable } from '../utils';
import { useError } from './error-context';

type AuthContextType = {
    tokens: {
        sessionToken: string;
        reactToken: string;
    };
    setSessionToken: (token: string) => void;
    setUserInfo: React.Dispatch<SetStateAction<JWTResponseDTO['user'] | null>>;
    isAuthenticated: boolean;
    isAuthLoading: boolean;
    userInfo: JWTResponseDTO['user'] | null;
    setSessionTokenLoading: React.Dispatch<React.SetStateAction<boolean>>;
    logout: () => void;
    goToPlatform: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const platformURL = getEnvironmentVariable('REACT_APP_PLATFORM_URL');
const LOGIN_URL = `${platformURL}/login`;
const ADMIN_URL = `${platformURL}/admin`;

export const AuthProvider: React.FC<{
    children: React.ReactNode
}> = ({ children }) => {

    const { showError } = useError();

    const [ tokens, setTokens ] = useState({
        reactToken: '',
        sessionToken: '',
    });

    const [ sessionTokenLoading, setSessionTokenLoading ] = useState(true);
    const [ reactTokenLoading, setReactTokenLoading ] = useState(true);
    const [ userInfo, setUserInfo ] = useState<JWTResponseDTO['user'] | null>(null);
    const isInitiated = useRef(false);

    useEffect(() => {
        if (isInitiated.current) {
            return;
        }

        isInitiated.current = true;

        const reactToken = localStorage.getItem('reactToken') || '';
        const sessionToken = localStorage.getItem('sessionToken') || '';
        const user = JSON.parse(localStorage.getItem('user') || '{}');

        const tokenChecks: Promise<any>[] = [];

        if (sessionToken && Object.keys(user).length > 0) {
            tokenChecks.push(
                validateToken(sessionToken, 'AuthorizationUser')
                .then((isValid) => {
                    if (isValid) {
                        setTokens(t => (
                            { ...t, sessionToken }
                        ));
                        setAuthToken('sessionToken', sessionToken);
                        // @ts-ignore
                        setUserInfo(user);
                    } else {
                        resetSessionInfo();
                    }
                })
                .catch((e) => {
                    if (e?.status === 401) {
                        resetSessionInfo();
                    } else {
                        showError({
                            code: e?.response?.status || 500,
                            message: 'API Error',
                            description: 'Failed to validate session token',
                            showRefresh: true,
                        });
                    }
                }),
            );
        } else {
            resetSessionInfo();
        }

        if (reactToken) {
            tokenChecks.push(
                validateToken(reactToken)
                .then((isValid) => {
                    if (isValid) {
                        setTokens(t => (
                            { ...t, reactToken }
                        ));
                        setAuthToken('reactToken', reactToken);
                    } else {
                        resetReactInfo();
                    }
                })
                .catch((e) => {
                    if (e?.status === 401) {
                        resetReactInfo();
                        window.location.reload();
                    } else {
                        showError({
                            code: e?.response?.status || 500,
                            message: 'API Error',
                            description: 'Failed to validate react token',
                            showRefresh: true,
                        });
                    }
                }),
            );
        } else {
            tokenChecks.push(
                getReactToken()
                .then(({ token }) => {
                    setTokens(t => (
                        { ...t, reactToken: token }
                    ));
                    localStorage.setItem('reactToken', token);
                    setAuthToken('reactToken', token);
                })
                .catch((err) => {
                    showError({
                        code: err?.response?.status || 500,
                        message: 'API Error',
                        description: 'Failed to get react token',
                        showRefresh: true,
                    });
                    resetReactInfo();
                }),
            );
        }

        Promise.all(tokenChecks).finally(() => {
            setSessionTokenLoading(false);
            setReactTokenLoading(false);
        });

    }, [ showError ]);

    const setSessionToken = useCallback((sessionToken: string) => {
        setTokens(tokens => (
            { ...tokens, sessionToken }
        ));
        setAuthToken('sessionToken', sessionToken);
    }, []);

    const resetReactInfo = () => {
        localStorage.removeItem('reactToken');
        setTokens(tokens => (
            {
                reactToken: '',
                sessionToken: tokens.sessionToken,
            }
        ));
    };

    const resetSessionInfo = () => {
        localStorage.removeItem('sessionToken');
        localStorage.removeItem('user');
        setTokens(tokens => (
            {
                reactToken: tokens.reactToken,
                sessionToken: '',
            }
        ));
        setUserInfo(null);
    };

    const onLogout = useCallback(() => {
        logoutSession().finally(() => {
            resetSessionInfo();
            window.location.href = LOGIN_URL;
        });
    }, []);

    const onGoToPlatform = () => {
        window.open(ADMIN_URL, '_blank');
    };

    return (
        <AuthContext.Provider
            value={{
                tokens,
                setSessionToken,
                isAuthenticated: !!tokens.reactToken,
                isAuthLoading: sessionTokenLoading || reactTokenLoading,
                userInfo,
                setUserInfo,
                setSessionTokenLoading,
                logout: onLogout,
                goToPlatform: onGoToPlatform,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return ctx;
};